// react
import { MouseEvent } from 'react';

// types
import { Severity } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// mui
import {
	Box,
	Chip,
	ChipProps,
	Grid,
	Paper,
	PaperProps,
	SxProps,
	Typography,
	TypographyProps,
} from '@mui/material';

// ui
import { ChipStatus, ChipStatusProps } from '../../ui/ChipStatus/ChipStatus';
import { Empty } from '../../ui/Empty/Empty';
import { IconCircle } from '../../ui/IconCircle/IconCircle';
import { Skeleton } from '../../ui/Skeleton/Skeleton';

// dialogs
import {
	DialogUtilization,
	DialogUtilizationProps,
} from '../../dialogs/DialogUtilization/DialogUtilization';
import {
	DialogTraceability,
	DialogTraceabilityProps,
} from '../../dialogs/DialogTraceability/DialogTraceability';

// theme
import { colorsSeverity, theme } from '../../../theme';

// props
export interface CardKeyStatProps {
	ChipStatusProps?: ChipStatusProps;
	footnote?: TypographyProps;
	lineItems?: {
		label: string;
		value: number | string;
	}[];
	severity?: null | Severity;
	title: TypographyProps;
	value: number | string;
}

export interface CardKeyStatsProps {
	color?: 'blue' | 'grey';
	dialog?: 'TRACEABILITY' | 'UTILIZATION';
	DialogProps?: Partial<DialogUtilizationProps> | Partial<DialogTraceabilityProps>;
	empty?: {
		ChipProps?: ChipProps;
		content: TypographyProps;
	};
	isActive?: boolean;
	isLoading?: boolean;
	stats: CardKeyStatProps[];
	title?: TypographyProps;
}

export const CardKeyStats = ({
	color = 'blue',
	dialog,
	empty,
	isActive = true,
	isLoading = false,
	DialogProps,
	stats,
	title,
}: CardKeyStatsProps) => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialog = searchParams.get('dialog');

	// vars
	const dialogId = DialogProps?.id || dialog;

	// handlers
	const handleOpenDialog = (e: MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
		e.stopPropagation();

		if (dialogId) {
			searchParams.set('dialog', dialogId);
			setSearchParams(searchParams);
		}
	};

	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		searchParams.delete('dialogTab');
		setSearchParams(searchParams);
	};

	// vars
	let colorHeaderBg: string | undefined = 'rgb(140, 216, 245)';
	let colorHeaderText: string | undefined = theme.palette.common.black;

	if (color === 'grey') {
		colorHeaderBg = theme.palette.brand.grey[100];
		colorHeaderText = theme.palette.brand.grey[800];
	}

	const hasDialog = dialog && DialogProps && isActive;
	const PaperProps: PaperProps = {};

	if (dialog && isActive) {
		PaperProps.onClick = handleOpenDialog;
	}

	let content = (
		<Box
			sx={{
				height: '100%',
				opacity: empty ? 0 : 1,
			}}>
			<Grid container sx={{ height: '100%' }}>
				{stats.map((stat, index) => {
					const { ChipStatusProps, footnote, lineItems, severity, title, value } = stat;
					const isLast = index === stats.length - 1;

					let colorBg = theme.palette.common.white;
					let colorText = theme.palette.brand.grey[800];

					if (severity) {
						colorBg = colorsSeverity[severity]?.background;
						colorText = colorsSeverity[severity]?.primary;
					}

					return (
						<Grid item key={`stat-${index}-${stat.value}`} xs={12} md={true}>
							<Box
								sx={(theme) => {
									let sx: SxProps = {
										backgroundColor: colorBg,
										padding: {
											xs: theme.spacing(2),
											lg: theme.spacing(3),
										},
										color: colorText,
										height: '100%',
									};

									if (!isLast) {
										sx = {
											...sx,
											borderBottomColor: theme.palette.brand.grey[300],
											borderBottomStyle: 'solid',
											borderBottomWidth: 1,
											[theme.breakpoints.up('md')]: {
												...sx,
												borderBottom: 'none',
												borderRightColor: theme.palette.brand.grey[300],
												borderRightStyle: 'solid',
												borderRightWidth: 1,
											},
										};
									}

									return sx;
								}}>
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<Grid justifyContent="space-between" container spacing={2}>
											<Grid item>
												<Typography component="div" fontWeight={600} variant="h1">
													{value}
												</Typography>
											</Grid>
											{ChipStatusProps && (
												<Grid item>
													<ChipStatus {...ChipStatusProps} />
												</Grid>
											)}
										</Grid>
										<Typography fontWeight={600} {...title} />
									</Grid>
									{lineItems && lineItems.length > 0 && (
										<Grid item xs={12}>
											<Grid container>
												{lineItems.map((item, i) => (
													<Grid item key={`line-item-${i}-${item.value}`} xs={12}>
														<Grid container spacing={1}>
															<Grid item>
																<Typography fontWeight={600} variant="caption">
																	{isActive ? item.value : <Skeleton width={'20px'} />}
																</Typography>
															</Grid>
															<Grid item>
																<Typography
																	fontWeight={600}
																	sx={{ opacity: 0.7 }}
																	variant="caption">
																	{item.label}
																</Typography>
															</Grid>
														</Grid>
													</Grid>
												))}
											</Grid>
										</Grid>
									)}
									{footnote && (
										<Grid item xs={12}>
											<Typography sx={{ opacity: 0.7 }} variant="caption" {...footnote} />
										</Grid>
									)}
								</Grid>
							</Box>
						</Grid>
					);
				})}
			</Grid>
		</Box>
	);

	if (isLoading) {
		content = (
			<Empty hasContent={false} hasTitle={false} isLoading={isLoading} position="absolute" />
		);
	} else if (empty) {
		content = (
			<Box
				sx={{
					position: 'absolute',
					top: '50%',
					left: '50%',
					transform: 'translate(-50%, -50%)',
				}}>
				<Grid container alignItems="center" direction="column" spacing={1}>
					<Grid item>
						<Chip label={t('comingSoon')} size="small" variant="twoTone" {...empty.ChipProps} />
					</Grid>
					<Grid item>
						<Typography
							children={t('comingSoon.description')}
							sx={{ textAlign: 'center' }}
							{...empty.content}
						/>
					</Grid>
				</Grid>
			</Box>
		);
	}

	return (
		<>
			<Paper
				sx={(theme) => {
					let sx: SxProps = {
						borderColor: theme.palette.brand.grey[300],
						borderStyle: 'solid',
						borderWidth: 1,
						height: '100%',
						overflow: 'hidden',
						position: 'relative',
					};

					if (hasDialog && isActive) {
						sx = {
							...sx,
							cursor: 'pointer',
						};
					}

					return sx;
				}}
				{...PaperProps}>
				<Grid container direction="column" sx={{ gap: 0, height: `100%` }}>
					{title && (
						<Grid item>
							<Box
								sx={(theme) => {
									return {
										borderBottomColor: theme.palette.brand.grey[300],
										borderBottomStyle: 'solid',
										borderBottomWidth: 1,
										backgroundColor: colorHeaderBg,
										padding: theme.spacing(2),
										color: colorHeaderText,
									};
								}}>
								<Grid container justifyContent={hasDialog ? 'flex-start' : 'center'} spacing={2}>
									<Grid item sx={{ marginLeft: hasDialog ? 'auto' : 0 }}>
										<Typography fontWeight={600} sx={{ fontSize: '1.6rem' }} {...title} />
									</Grid>
									{hasDialog && (
										<Grid item sx={{ marginLeft: 'auto' }}>
											<IconCircle
												scheme="dark"
												size="xs"
												sxBox={{ borderColor: 'currentColor', color: 'currentColor' }}
												type="help"
												variant="outlined"
											/>
										</Grid>
									)}
								</Grid>
							</Box>
						</Grid>
					)}
					<Grid flex={1} item sx={{ position: 'relative' }}>
						{content}
					</Grid>
				</Grid>
			</Paper>
			{dialog === 'TRACEABILITY' && (
				<DialogTraceability
					{...(DialogProps as DialogTraceabilityProps)}
					isOpen={paramDialog === dialogId}
					onClose={handleCloseDialog}
				/>
			)}
			{dialog === 'UTILIZATION' && (
				<DialogUtilization
					{...(DialogProps as DialogUtilizationProps)}
					isOpen={paramDialog === dialogId}
					onClose={handleCloseDialog}
				/>
			)}
		</>
	);
};
